import { Tabs } from '@design-systems/canvas-ui'
import { MPMLayout } from 'components/layout/layout'
import { PageTabs } from 'components/page-tabs'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import { useMemo, useState, useEffect } from 'react'
import { useLogClick } from 'utils/analytics'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useStoreFulfillment } from 'hooks/store-fulfillment/useStoreFulfillment'
import { useEssentials } from 'hooks/utils/useEssentials'
import { BasicPlaceholder } from 'components/placeholder'
import useCountdown from 'hooks/utils/useCountdown'
import { useFlags } from '../../../firebase/FirebaseRemoteConfig.provider'

const ROUTES = {
    PICKUP: 'pickup',
    SHIP: 'ship',
    INF: 'inf',
    NDD: 'ndd',
    CAPABILITIES: 'capabilities',
}

export const StoreFulfillmentPage = () => {
    const { t, env, session, logout, currentLocation } = useEssentials()
    const navigate = useNavigate()
    const location = useLocation()
    const { storeFulfillment } = TRANSLATION_KEYS
    const { tabs: tabsKeys } = storeFulfillment
    const { logClick } = useLogClick('StoreFulfillment')
    const { nddTabEnabled } = useFlags()

    const [activeTab, setActiveTab] = useState<string>(
        location.pathname.split('/').pop() || ROUTES.PICKUP,
    )

    const handleTabSelect = (activeTab: string) => {
        const searchParams = new URLSearchParams(location.search)
        setActiveTab(activeTab)
        logClick('store fulfillment tab ' + activeTab)
        void navigate(
            `/store-fulfillment/${activeTab}?${searchParams.toString()}`,
        )
    }

    useEffect(() => {
        const newTab = location.pathname.split('/').pop() || ROUTES.PICKUP
        setActiveTab(newTab)
    }, [location.pathname])

    const tabsWithTranslations = useMemo(() => {
        const tabs = [
            { key: ROUTES.PICKUP, label: t(tabsKeys.pickup) },
            { key: ROUTES.SHIP, label: t(tabsKeys.ship) },
        ]

        if (nddTabEnabled) {
            tabs.push({ key: ROUTES.NDD, label: t(tabsKeys.ndd) })
        }

        tabs.push({ key: ROUTES.INF, label: t(tabsKeys.inf) })
        tabs.push({ key: ROUTES.CAPABILITIES, label: t(tabsKeys.capabilities) })

        return tabs
    }, [
        t,
        tabsKeys.pickup,
        tabsKeys.ship,
        tabsKeys.inf,
        tabsKeys.capabilities,
        tabsKeys.ndd,
        nddTabEnabled,
    ])

    const { data, isLoading, isError, error, refetch, remove } =
        useStoreFulfillment({
            env,
            accessToken: session?.accessToken ?? '',
            location: currentLocation,
        })

    const count = useCountdown(5, logout, error as Error)

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(storeFulfillment.title)}
                />
            )}
        >
            <MPMLayout
                heading={t(storeFulfillment.title)}
                className="hc8-ph-none"
                hasTabs
                tabs={
                    <PageTabs
                        activeTab={activeTab}
                        className="fulfillment-tabs hc8-pv-2x"
                        disableAdaptive={true}
                        onTabSelect={handleTabSelect}
                    >
                        {tabsWithTranslations.map((tab) => (
                            <Tabs.Item key={tab.key} name={tab.key}>
                                {tab.label}
                            </Tabs.Item>
                        ))}
                    </PageTabs>
                }
            >
                <div className="hc8-ph-4x">
                    {isLoading ? (
                        <div
                            className="placeholder-container"
                            data-testid="placeholder"
                        >
                            <BasicPlaceholder
                                type="table"
                                rows={5}
                                columns={3}
                            />
                        </div>
                    ) : isError || !data ? (
                        <ErrorAlert
                            error={error}
                            count={count}
                            onRetry={() => refetch()}
                        />
                    ) : (
                        <Outlet context={{ data, refetch, remove }} />
                    )}
                </div>
            </MPMLayout>
        </ErrorBoundary>
    )
}
